import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppExtraArgument, AppState } from '@src/providers/AppStateProvider/types';
import type { DealBookingType } from '@src/types/OTC';
import { openView } from '../../OMSSlice';
import { OMSView, type OMSViewLaunchDataMap } from '../../OMSView';
import { manualDealSliceActions } from '../ManualDealSlice';

export interface OpenManualDealTradeFormParams {
  launchData?: OMSViewLaunchDataMap[typeof OMSView.ManualDealTradeForm];
}

/**
 * Thunk to open ManualDealTradeForm view and initialize the form state with booking type.
 *
 * Booking type priority:
 * 1. If a deal is being modified, use the booking type already set by modifyDeal (don't override)
 * 2. Otherwise, use booking type from launchData
 * 3. Otherwise, default to 'forward'
 *
 * This ensures the booking type is set atomically when the view opens, avoiding the need for useEffect.
 */
export const openManualDealTradeFormThunk = createAsyncThunk<
  void,
  OpenManualDealTradeFormParams,
  { state: AppState; extra: AppExtraArgument }
>('oms/openManualDealTradeFormThunk', async (params, thunkAPI) => {
  const { launchData } = params;
  const { dispatch, getState } = thunkAPI;

  // Check if a deal is being modified (modifyDeal already sets the booking type)
  const manualDealState = getState().manualDeal;
  const dealBeingModified = manualDealState.dealBeingModified;
  const currentBookingType = manualDealState.form.bookingTypeField.value;

  // Determine booking type: prioritize deal being modified, then launchData, then default
  let bookingTypeToSet: DealBookingType;

  if (dealBeingModified && currentBookingType) {
    // Deal is being modified - use the booking type already set by modifyDeal
    bookingTypeToSet = currentBookingType;
  } else if (launchData?.bookingType) {
    // Use booking type from launchData
    bookingTypeToSet = launchData.bookingType;
  } else {
    // Default to 'forward'
    bookingTypeToSet = 'forward';
  }

  // Open the view with launchData that includes the determined booking type
  // This ensures viewData?.bookingType matches what we set in Redux, preventing useEffect from overriding
  dispatch(
    openView({
      view: OMSView.ManualDealTradeForm,
      launchData: {
        ...launchData,
        bookingType: bookingTypeToSet,
      },
    })
  );

  // Initialize form state with booking type
  dispatch(manualDealSliceActions.markReady());
  dispatch(manualDealSliceActions.setBookingType(bookingTypeToSet));
});
